import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Switch,
  Upload,
  Row,
  Col,
  Divider,
  Steps,
  Space,
  Alert,
  message,
  Modal,
  Collapse,
  Tag,
  Checkbox,
  Select,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined,
  WhatsAppOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useMyContext } from "Context/MyContextProvider";
import apiClient from "services/apiClient";
import { useNavigate } from "react-router-dom";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Step } = Steps;
const { Panel } = Collapse;

const NewFlow = () => {
  const {} = useMyContext();
  // Form instance
  const [form] = Form.useForm();
  // const uploadPublicKey = async () => {
  //   const publicKeyContent = "CmgKJAjbqZijhq3gAxIGZW50OndhIgtTTVMgRk9SIFlPVVD55/HEBhpA38phHVo7nAEODxGdRVl7ouDCNAOIjUudtMSNDme0ou8T2Qenku1HELIxsrGr3H0JlAgNhnRnX4lq7MfX13+aABIubR9nw/jznAbgRIezkKxtKZBc4eBZwNjkoT1FTq082y8h+gsHbvS2IrKymhq7OA=="

  //   try {
  //     const response = await apiClient.post(
  //       `https://graph.facebook.com/v25.0/${phone_id}`,
  //       {
  //         flows_public_key: publicKeyContent
  //       },
  //       {
  //         headers: {
  //           'Authorization': `Bearer ${waToken}`,
  //           'Content-Type': 'application/json'
  //         }
  //       }
  //     )
  //     console.log('Public key uploaded:', response.data)
  //   } catch (error) {
  //     console.error('Failed to upload public key:', error.response?.data)
  //   }
  // }

  // Flow publish test removed — use metaUrls.publishFlow(flowId) when needed
  // // UI state
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  // Form state
  const [flowName, setFlowName] = useState("");
  const [description, setDescription] = useState("");
  const [entryPoint, setEntryPoint] = useState("cta");
  const [flowType, setFlowType] = useState("step_by_step");
  const [selectedLanguages, setSelectedLanguages] = useState(["en_US"]);
  const [screens, setScreens] = useState([]);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Initialize and sync form with state
  useEffect(() => {
    // Initial sync of form values with state
    const initialValues = form.getFieldsValue();
    setFlowName(initialValues.flowName || "");
    setDescription(initialValues.description || "");
    setEntryPoint(initialValues.entryPoint || "cta");
    setFlowType(initialValues.flowType || "step_by_step");

    // Handle languages properly
    if (initialValues.languages) {
      const newLanguages = Array.isArray(initialValues.languages)
        ? initialValues.languages
        : [initialValues.languages];
      setSelectedLanguages(newLanguages.length > 0 ? newLanguages : ["en_US"]);
    } else {
      setSelectedLanguages(["en_US"]);
    }

    setScreens(initialValues.screens || []);
    setAcceptedTerms(initialValues.terms || false);
  }, [form]); // Add form as dependency

  // Sync form fields when state changes
  useEffect(() => {
    form.setFieldsValue({
      flowName: flowName,
      description: description,
      entryPoint: entryPoint,
      flowType: flowType,
      languages: selectedLanguages.length > 0 ? selectedLanguages[0] : "en_US",
      screens: screens,
      terms: acceptedTerms,
    });
  }, [
    form,
    flowName,
    description,
    entryPoint,
    flowType,
    selectedLanguages,
    screens,
    acceptedTerms,
  ]);

  // Static data with validation rules
  const languageOptions = [
    { code: "en_US", name: "English (US)" },
    { code: "es_ES", name: "Spanish (Spain)" },
    { code: "pt_BR", name: "Portuguese (Brazil)" },
    { code: "hi_IN", name: "Hindi (India)" },
    { code: "de_DE", name: "German (Germany)" },
    { code: "fr_FR", name: "French (France)" },
    { code: "it_IT", name: "Italian (Italy)" },
    { code: "ar_AR", name: "Arabic" },
  ];

  const entryPoints = [
    { value: "cta", label: "Call-to-Action Button" },
    { value: "menu", label: "Menu Option" },
    { value: "auto_reply", label: "Auto Reply" },
    { value: "custom", label: "Custom Trigger" },
    { value: "quick_reply", label: "Quick Reply" },
  ];

  const flowTypes = [
    { value: "step_by_step", label: "Step by Step" },
    { value: "single_screen", label: "Single Screen" },
    { value: "custom", label: "Custom Flow" },
  ];

  const componentTypes = [
    { value: "text_input", label: "Text Input" },
    { value: "email_input", label: "Email Input" },
    { value: "phone_input", label: "Phone Input" },
    { value: "radio", label: "Radio Button" },
    { value: "checkbox", label: "Checkbox" },
    { value: "dropdown", label: "Dropdown" },
    { value: "date_picker", label: "Date Picker" },
    { value: "button", label: "Button" },
  ];

  const buttonActions = [
    { value: "submit", label: "Submit Form" },
    { value: "next", label: "Next Step" },
    { value: "back", label: "Go Back" },
    { value: "custom", label: "Custom Action" },
  ];

  // Validation functions
  const validateFlowName = (rule, value) => {
    if (!value) return Promise.reject("Flow name is required");
    if (value.length > 100)
      return Promise.reject("Flow name must be 100 characters or less");
    if (!/^[a-zA-Z0-9_\-\s]+$/.test(value)) {
      return Promise.reject(
        "Only alphanumeric characters, underscores, hyphens, and spaces allowed"
      );
    }
    return Promise.resolve();
  };

  const validateDescription = (rule, value) => {
    if (value && value.length > 500) {
      return Promise.reject("Description must be 500 characters or less");
    }
    return Promise.resolve();
  };

  const validateScreenId = (rule, value) => {
    if (!value) return Promise.reject("Screen ID is required");
    if (value.length > 50)
      return Promise.reject("Screen ID must be 50 characters or less");
    if (!/^[a-zA-Z0-9_]+$/.test(value)) {
      return Promise.reject(
        "Screen ID must be a valid slug format (alphanumeric and underscores only)"
      );
    }
    return Promise.resolve();
  };

  const validateScreenTitle = (rule, value) => {
    if (!value) return Promise.reject("Screen title is required");
    if (value.length > 80)
      return Promise.reject("Screen title must be 80 characters or less");
    if (!/^[a-zA-Z0-9\s]+$/.test(value)) {
      return Promise.reject(
        "Screen title can only contain alphanumeric characters and spaces"
      );
    }
    return Promise.resolve();
  };

  const validateComponentName = (rule, value) => {
    if (!value) return Promise.reject("Component name is required");
    if (/[^a-zA-Z0-9_]/.test(value)) {
      return Promise.reject(
        "Component name must not include special characters or spaces"
      );
    }
    return Promise.resolve();
  };

  const validateTextInputMaxLength = (rule, value) => {
    if (value && (value < 1 || value > 120)) {
      return Promise.reject("Max length must be between 1 and 120 characters");
    }
    return Promise.resolve();
  };

  const validateEmailFormat = (rule, value) => {
    if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return Promise.reject("Please enter a valid email format");
    }
    return Promise.resolve();
  };

  // Advanced validation for flow structure
  const validateFlowStructure = (values) => {
    const errors = [];
    console.log("Validating flow structure with values:", values);

    // Check for at least one screen
    if (
      !values.screens ||
      !Array.isArray(values.screens) ||
      values.screens.length === 0
    ) {
      errors.push("Flow must have at least one screen");
      return errors; // Return early if we don't have any screens
    } else {
      // Check for unique screen IDs
      const screenIds = values.screens
        .map((screen) => screen.id)
        .filter(Boolean);
      const uniqueIds = [...new Set(screenIds)];
      if (screenIds.length !== uniqueIds.length) {
        errors.push("All screen IDs must be unique");
      }

      // Check max screens limit
      if (values.screens.length > 20) {
        errors.push("Maximum 20 screens allowed per flow");
      }

      // Validate each screen
      values.screens.forEach((screen, index) => {
        if (!screen.components || screen.components.length === 0) {
          errors.push(`Screen ${index + 1} must have at least one component`);
        }

        // Check button limit per screen
        const buttons =
          screen.components?.filter((comp) => comp.type === "button") || [];
        if (buttons.length > 3) {
          errors.push(`Screen ${index + 1} can have maximum 3 buttons`);
        }

        // Validate components
        screen.components?.forEach((component, compIndex) => {
          if (component.type === "radio" || component.type === "dropdown") {
            if (!component.options || component.options.length < 2) {
              errors.push(
                `${component.type} in screen ${
                  index + 1
                } must have at least 2 options`
              );
            }
          }
          if (component.type === "checkbox") {
            if (!component.options || component.options.length < 1) {
              errors.push(
                `Checkbox in screen ${index + 1} must have at least 1 option`
              );
            }
          }
        });
      });

      // Check for cyclic navigation
      const navigationMap = {};
      values.screens.forEach((screen) => {
        if (screen.next_step_id && screen.next_step_id === screen.id) {
          errors.push(`Screen "${screen.id}" cannot navigate to itself`);
        }
      });
    }

    return errors;
  };

  // Sync form values with state
  const updateFormState = (changedValues, allValues) => {
    if ("flowName" in changedValues) setFlowName(changedValues.flowName || "");
    if ("description" in changedValues)
      setDescription(changedValues.description || "");
    if ("entryPoint" in changedValues)
      setEntryPoint(changedValues.entryPoint || "cta");
    if ("flowType" in changedValues)
      setFlowType(changedValues.flowType || "step_by_step");
    if ("languages" in changedValues) {
      const newLanguages = Array.isArray(changedValues.languages)
        ? changedValues.languages
        : changedValues.languages
        ? [changedValues.languages]
        : ["en_US"];
      setSelectedLanguages(newLanguages);
    }
    if ("screens" in changedValues) {
      const updatedScreens = Array.isArray(changedValues.screens)
        ? changedValues.screens
        : [];
      setScreens((prevScreens) => {
        const merged = JSON.parse(JSON.stringify(prevScreens)); // deep copy

        updatedScreens.forEach((scr, si) => {
          if (!merged[si]) merged[si] = {};

          Object.entries(scr || {}).forEach(([key, value]) => {
            if (key === "components" && Array.isArray(value)) {
              merged[si].components = merged[si].components || [];

              value.forEach((comp, ci) => {
                if (!merged[si].components[ci]) merged[si].components[ci] = {};

                Object.entries(comp || {}).forEach(([ckey, cvalue]) => {
                  if (ckey === "options" && Array.isArray(cvalue)) {
                    merged[si].components[ci].options =
                      merged[si].components[ci].options || [];
                    cvalue.forEach((opt, oi) => {
                      merged[si].components[ci].options[oi] = {
                        ...merged[si].components[ci].options[oi],
                        ...opt,
                      };
                    });
                  } else if (cvalue !== undefined) {
                    merged[si].components[ci][ckey] = cvalue;
                  }
                });
              });
            } else if (value !== undefined) {
              merged[si][key] = value;
            }
          });
        });

        return merged;
      });
      // If screens were added, clear any validation errors about missing screens
      if (changedValues.screens && changedValues.screens.length > 0) {
        setValidationErrors((prev) =>
          prev.filter((error) => error !== "Flow must have at least one screen")
        );
      }
    }
    if ("terms" in changedValues)
      setAcceptedTerms(changedValues.terms || false);
  };
  // Handle step navigation with validation
  const nextStep = async () => {
    try {
      // Validate current step fields
      await form.validateFields();

      // Update state with current form values to keep state in sync
      const values = form.getFieldsValue();
      setFlowName(values.flowName || flowName);
      setDescription(values.description || description);
      setEntryPoint(values.entryPoint || entryPoint);
      setFlowType(values.flowType || flowType);

      // Handle languages consistently
      if (values.languages) {
        const newLanguages = Array.isArray(values.languages)
          ? values.languages
          : [values.languages];
        setSelectedLanguages(
          newLanguages.length > 0 ? newLanguages : selectedLanguages
        );
      }

      setScreens(values.screens || screens);
      setAcceptedTerms(values.terms || acceptedTerms);

      // Move to next step
      setCurrentStep(currentStep + 1);
      setValidationErrors([]);
    } catch (error) {
      console.log("Validation failed:", error);
      message.error("Please fill in all required fields correctly");
    }
  };

  const prevStep = () => {
    // When moving back, we don't need to validate, but we should update state
    const values = form.getFieldsValue();
    setFlowName(values.flowName || flowName);
    setDescription(values.description || description);
    setEntryPoint(values.entryPoint || entryPoint);
    setFlowType(values.flowType || flowType);

    // Handle languages consistently
    if (values.languages) {
      const newLanguages = Array.isArray(values.languages)
        ? values.languages
        : [values.languages];
      setSelectedLanguages(
        newLanguages.length > 0 ? newLanguages : selectedLanguages
      );
    }

    setScreens(values.screens || screens);
    setAcceptedTerms(values.terms || acceptedTerms);

    setCurrentStep(currentStep - 1);
    setValidationErrors([]);
  };

  // Handle preview modal
  const showPreview = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      const errors = validateFlowStructure(values);

      if (errors.length > 0) {
        setValidationErrors(errors);
        message.error("Please fix validation errors before previewing");
        return;
      }

      // Update all state variables with current form values
      setFlowName(values.flowName);
      setDescription(values.description || "");
      setEntryPoint(values.entryPoint);
      setFlowType(values.flowType);

      // Handle languages properly
      const newLanguages = values.languages
        ? Array.isArray(values.languages)
          ? values.languages
          : [values.languages]
        : ["en_US"];
      setSelectedLanguages(newLanguages);

      setScreens(values.screens || []);
      setAcceptedTerms(values.terms || false);

      setPreviewVisible(true);
    } catch (error) {
      console.log("Validation failed:", error);
      message.error("Please complete all required fields");
    }
  };

  // Dynamic form operations
  const addScreen = () => {
    const currentScreens = form.getFieldValue("screens") || [];
    if (currentScreens.length >= 20) {
      message.warning("Maximum 20 screens allowed per flow");
      return;
    }

    // Create a new screen with required defaults
    const newScreens = [
      ...currentScreens,
      {
        id: `screen_${currentScreens.length + 1}`,
        title: `Screen ${currentScreens.length + 1}`,
        components: [
          {
            id: `component_${Date.now()}`,
            type: "text_input",
            name: `field_1`,
            placeholder: "Enter text here",
            required: true,
          },
        ],
      },
    ];

    // Update both form and state
    form.setFieldsValue({ screens: newScreens });
    setScreens(newScreens);
    console.log("Added screen, new screens:", newScreens);

    // Clear any validation errors
    setValidationErrors([]);
  };

  const addComponent = (screenIndex) => {
    const currentScreens = form.getFieldValue("screens") || [];
    const screen = currentScreens[screenIndex];

    if (screen) {
      // Create a deep copy of screens to avoid mutation issues
      const updatedScreens = JSON.parse(JSON.stringify(currentScreens));
      updatedScreens[screenIndex].components =
        updatedScreens[screenIndex].components || [];
      updatedScreens[screenIndex].components.push({
        id: `component_${Date.now()}`,
        type: "text_input",
        name: `field_${updatedScreens[screenIndex].components.length + 1}`,
        placeholder: "",
        required: true,
      });

      // Update both form and state
      form.setFieldsValue({ screens: updatedScreens });
      setScreens(updatedScreens);
    }
  };

  const addOption = (screenIndex, componentIndex) => {
    const currentScreens = form.getFieldValue("screens") || [];
    const component = currentScreens[screenIndex]?.components[componentIndex];

    if (component) {
      // Create a deep copy of screens to avoid mutation issues
      const updatedScreens = JSON.parse(JSON.stringify(currentScreens));
      updatedScreens[screenIndex].components[componentIndex].options =
        updatedScreens[screenIndex].components[componentIndex].options || [];

      updatedScreens[screenIndex].components[componentIndex].options.push({
        id: `option_${Date.now()}`,
        value: "",
        label: "",
      });

      // Update both form and state
      form.setFieldsValue({ screens: updatedScreens });
      setScreens(updatedScreens);
    }
  };

  // Flow form steps
  const steps = [
    {
      title: "Basic Information",
      content: (
        <Card title="Flow Basic Information" bordered={false}>
          <Alert
            message="WhatsApp Flow Setup"
            description="Create interactive flows that comply with Meta's validation requirements. All required fields must be completed correctly."
            type="info"
            showIcon
            style={{ marginBottom: 20 }}
          />

          {validationErrors.length > 0 && (
            <Alert
              message="Validation Errors"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              }
              type="error"
              showIcon
              style={{ marginBottom: 20 }}
            />
          )}

          <Form.Item
            name="flowName"
            label="Flow Name"
            required={true}
            rules={[{ validator: validateFlowName }]}
            tooltip="Max 100 characters, alphanumeric, underscores, hyphens, and spaces allowed"
          >
            <Input
              placeholder="My Customer Support Flow"
              maxLength={100}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (Optional)"
            required={false}
            rules={[{ validator: validateDescription }]}
            tooltip="Max 500 characters"
          >
            <TextArea
              rows={4}
              placeholder="Describe what this flow is used for"
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="entryPoint"
            label="Entry Point"
            rules={[
              { required: true, message: "Please select an entry point" },
            ]}
            tooltip="How users will access this flow"
          >
            <Select placeholder="Select entry point">
              {entryPoints.map((entry) => (
                <Option key={entry.value} value={entry.value}>
                  {entry.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="flowType"
            label="Flow Type"
            rules={[{ required: true, message: "Please select a flow type" }]}
          >
            <Select placeholder="Select flow type">
              {flowTypes.map((type) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="languages"
            label="Languages"
            rules={[
              {
                required: true,
                message: "Please select at least one language",
              },
            ]}
            tooltip="BCP-47 language codes"
          >
            <Select
              placeholder="Select languages"
              showSearch
              value={selectedLanguages[0]} // Use first language as the value
              onChange={(value) => {
                const newLanguages = value ? [value] : ["en_US"];
                setSelectedLanguages(newLanguages);
                form.setFieldsValue({ languages: value });
              }}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {languageOptions.map((lang) => (
                <Option key={lang.code} value={lang.code}>
                  {lang.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Card>
      ),
    },
    {
      title: "Screens & Components",
      content: (
        <Card title="Flow Screens & Components" bordered={false}>
          <Alert
            message="Screen Requirements"
            description="At least 1 screen required. Max 20 screens. Each screen needs unique ID (max 50 chars) and at least 1 component. Max 3 buttons per screen."
            type="info"
            showIcon
            style={{ marginBottom: 20 }}
          />

          {validationErrors.length > 0 && (
            <Alert
              message="Validation Errors"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              }
              type="error"
              showIcon
              style={{ marginBottom: 20 }}
            />
          )}

          <Form.List name="screens">
            {(fields, { add, remove }) => (
              <>
                {fields.length === 0 ? (
                  <Alert
                    message="No Screens Added"
                    description="Click 'Add Screen' to create your first screen"
                    type="warning"
                    showIcon
                    style={{ marginBottom: 20 }}
                  />
                ) : (
                  fields.map((field, index) => (
                    <Card
                      key={field.key}
                      title={<span>Screen {index + 1}</span>}
                      style={{ marginBottom: 20 }}
                      extra={
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                        >
                          Remove
                        </Button>
                      }
                    >
                      <Form.Item
                        name={[field.name, "id"]}
                        label="Screen ID"
                        rules={[{ validator: validateScreenId }]}
                        tooltip="Unique identifier, max 50 chars, alphanumeric and underscores only"
                      >
                        <Input
                          placeholder="screen_1"
                          maxLength={50}
                          showCount
                        />
                      </Form.Item>

                      <Form.Item
                        name={[field.name, "title"]}
                        label="Screen Title"
                        rules={[{ validator: validateScreenTitle }]}
                        tooltip="Max 80 characters, alphanumeric and spaces only"
                      >
                        <Input
                          placeholder="Welcome Screen"
                          maxLength={80}
                          showCount
                        />
                      </Form.Item>

                      <Form.Item
                        name={[field.name, "next_step_id"]}
                        label="Next Step ID (Optional)"
                        tooltip="ID of the screen to navigate to next"
                      >
                        <Select placeholder="Select next screen" allowClear>
                          {(form.getFieldValue("screens") || []).map(
                            (screen, screenIndex) =>
                              screen?.id && screenIndex !== index ? (
                                <Option key={screen.id} value={screen.id}>
                                  {screen.id}
                                </Option>
                              ) : null
                          )}
                        </Select>
                      </Form.Item>

                      <Divider orientation="left">Components</Divider>

                      <Form.List name={[field.name, "components"]}>
                        {(componentFields, componentMeta) => (
                          <>
                            {componentFields.map(
                              (componentField, componentIndex) => (
                                <Card
                                  key={componentField.key}
                                  type="inner"
                                  title={`Component ${componentIndex + 1}`}
                                  style={{ marginBottom: 10 }}
                                  extra={
                                    <Button
                                      danger
                                      size="small"
                                      icon={<DeleteOutlined />}
                                      onClick={() =>
                                        componentMeta.remove(
                                          componentField.name
                                        )
                                      }
                                    />
                                  }
                                >
                                  <Form.Item
                                    name={[componentField.name, "type"]}
                                    label="Component Type"
                                    rules={[
                                      {
                                        required: true,
                                        message: "Please select component type",
                                      },
                                    ]}
                                  >
                                    <Select placeholder="Select component type">
                                      {componentTypes.map((type) => (
                                        <Option
                                          key={type.value}
                                          value={type.value}
                                        >
                                          {type.label}
                                        </Option>
                                      ))}
                                    </Select>
                                  </Form.Item>

                                  <Form.Item
                                    name={[componentField.name, "name"]}
                                    label="Component Name"
                                    rules={[
                                      { validator: validateComponentName },
                                    ]}
                                    tooltip="No special characters or spaces allowed"
                                  >
                                    <Input placeholder="field_name" />
                                  </Form.Item>

                                  <Form.Item
                                    noStyle
                                    shouldUpdate={(
                                      prevValues,
                                      currentValues
                                    ) => {
                                      const prevType =
                                        prevValues.screens?.[index]
                                          ?.components?.[componentIndex]?.type;
                                      const currentType =
                                        currentValues.screens?.[index]
                                          ?.components?.[componentIndex]?.type;
                                      return prevType !== currentType;
                                    }}
                                  >
                                    {({ getFieldValue }) => {
                                      const type = getFieldValue([
                                        "screens",
                                        index,
                                        "components",
                                        componentIndex,
                                        "type",
                                      ]);

                                      return (
                                        <>
                                          {(type === "text_input" ||
                                            type === "email_input" ||
                                            type === "phone_input") && (
                                            <>
                                              <Form.Item
                                                name={[
                                                  componentField.name,
                                                  "placeholder",
                                                ]}
                                                label="Placeholder"
                                                rules={[
                                                  {
                                                    required: true,
                                                    message:
                                                      "Placeholder is required",
                                                  },
                                                ]}
                                              >
                                                <Input placeholder="Enter placeholder text" />
                                              </Form.Item>

                                              {type === "text_input" && (
                                                <Form.Item
                                                  name={[
                                                    componentField.name,
                                                    "max_length",
                                                  ]}
                                                  label="Max Length"
                                                  rules={[
                                                    {
                                                      validator:
                                                        validateTextInputMaxLength,
                                                    },
                                                  ]}
                                                  tooltip="Between 1 and 120 characters"
                                                >
                                                  <Input
                                                    type="number"
                                                    min={1}
                                                    max={120}
                                                    placeholder="120"
                                                  />
                                                </Form.Item>
                                              )}

                                              <Form.Item
                                                name={[
                                                  componentField.name,
                                                  "required",
                                                ]}
                                                label="Required Field"
                                                valuePropName="checked"
                                              >
                                                <Switch defaultChecked />
                                              </Form.Item>
                                            </>
                                          )}

                                          {(type === "radio" ||
                                            type === "checkbox" ||
                                            type === "dropdown") && (
                                            <>
                                              <Form.Item
                                                name={[
                                                  componentField.name,
                                                  "required",
                                                ]}
                                                label="Required Field"
                                                valuePropName="checked"
                                              >
                                                <Switch defaultChecked />
                                              </Form.Item>

                                              <Divider orientation="left">
                                                Options
                                              </Divider>
                                              <Form.List
                                                name={[
                                                  componentField.name,
                                                  "options",
                                                ]}
                                              >
                                                {(optionFields, optionMeta) => (
                                                  <>
                                                    {optionFields.map(
                                                      (optionField) => (
                                                        <Row
                                                          key={optionField.key}
                                                          gutter={16}
                                                          align="middle"
                                                          style={{
                                                            marginBottom: 10,
                                                          }}
                                                        >
                                                          <Col span={10}>
                                                            <Form.Item
                                                              name={[
                                                                optionField.name,
                                                                "value",
                                                              ]}
                                                              rules={[
                                                                {
                                                                  required: true,
                                                                  message:
                                                                    "Value required",
                                                                },
                                                              ]}
                                                              noStyle
                                                            >
                                                              <Input placeholder="Option value" />
                                                            </Form.Item>
                                                          </Col>
                                                          <Col span={10}>
                                                            <Form.Item
                                                              name={[
                                                                optionField.name,
                                                                "label",
                                                              ]}
                                                              rules={[
                                                                {
                                                                  required: true,
                                                                  message:
                                                                    "Label required",
                                                                },
                                                              ]}
                                                              noStyle
                                                            >
                                                              <Input placeholder="Option label" />
                                                            </Form.Item>
                                                          </Col>
                                                          <Col span={4}>
                                                            <Button
                                                              danger
                                                              icon={
                                                                <DeleteOutlined />
                                                              }
                                                              onClick={() =>
                                                                optionMeta.remove(
                                                                  optionField.name
                                                                )
                                                              }
                                                              size="small"
                                                            />
                                                          </Col>
                                                        </Row>
                                                      )
                                                    )}

                                                    <Form.Item>
                                                      <Button
                                                        type="dashed"
                                                        onClick={() =>
                                                          addOption(
                                                            index,
                                                            componentIndex
                                                          )
                                                        }
                                                        block
                                                        icon={<PlusOutlined />}
                                                      >
                                                        Add Option (Min:{" "}
                                                        {type === "checkbox"
                                                          ? "1"
                                                          : "2"}
                                                        )
                                                      </Button>
                                                    </Form.Item>
                                                  </>
                                                )}
                                              </Form.List>
                                            </>
                                          )}

                                          {type === "button" && (
                                            <>
                                              <Form.Item
                                                name={[
                                                  componentField.name,
                                                  "label",
                                                ]}
                                                label="Button Label"
                                                rules={[
                                                  {
                                                    required: true,
                                                    message:
                                                      "Button label is required",
                                                  },
                                                ]}
                                              >
                                                <Input placeholder="Submit" />
                                              </Form.Item>

                                              <Form.Item
                                                name={[
                                                  componentField.name,
                                                  "action",
                                                ]}
                                                label="Button Action"
                                                rules={[
                                                  {
                                                    required: true,
                                                    message:
                                                      "Please select action",
                                                  },
                                                ]}
                                              >
                                                <Select placeholder="Select action">
                                                  {buttonActions.map(
                                                    (action) => (
                                                      <Option
                                                        key={action.value}
                                                        value={action.value}
                                                      >
                                                        {action.label}
                                                      </Option>
                                                    )
                                                  )}
                                                </Select>
                                              </Form.Item>
                                            </>
                                          )}

                                          {type === "date_picker" && (
                                            <Form.Item
                                              name={[
                                                componentField.name,
                                                "required",
                                              ]}
                                              label="Required Field"
                                              valuePropName="checked"
                                            >
                                              <Switch defaultChecked />
                                            </Form.Item>
                                          )}
                                        </>
                                      );
                                    }}
                                  </Form.Item>
                                </Card>
                              )
                            )}

                            <Form.Item>
                              <Button
                                type="dashed"
                                onClick={() => addComponent(index)}
                                block
                                icon={<PlusOutlined />}
                              >
                                Add Component (At least 1 required)
                              </Button>
                            </Form.Item>
                          </>
                        )}
                      </Form.List>
                    </Card>
                  ))
                )}

                <Form.Item>
                  <Button
                    // type="primary"
                    onClick={addScreen}
                    block
                    icon={<PlusOutlined />}
                    disabled={fields.length >= 20}
                    size="large"
                    style={{ height: "50px", fontSize: "16px" }}
                  >
                    Add Screen ({fields.length}/20)
                  </Button>

                  {fields.length === 0 && (
                    <Alert
                      message="Important"
                      description="You need to add at least one screen to create a flow"
                      type="warning"
                      showIcon
                      style={{ marginTop: 10 }}
                    />
                  )}
                </Form.Item>
              </>
            )}
          </Form.List>
        </Card>
      ),
    },
    {
      title: "Review & Submit",
      content: (
        <Card title="Review Your Flow" bordered={false}>
          <Alert
            message="Final Review"
            description="Review all details carefully. Flow will be sent to Meta for approval (typically 24-48 hours)."
            type="info"
            showIcon
            style={{ marginBottom: 20 }}
          />

          {validationErrors.length > 0 && (
            <Alert
              message="Validation Errors - Please Fix Before Submitting"
              description={
                <div>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                  {validationErrors.includes(
                    "Flow must have at least one screen"
                  ) && (
                    <div style={{ marginTop: 10, fontWeight: "bold" }}>
                      Click the "Add Screen" button below to create a screen.
                    </div>
                  )}
                </div>
              }
              type="error"
              showIcon
              style={{ marginBottom: 20 }}
            />
          )}

          <Form.Item
            name="terms"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("You must accept the terms and conditions")
                      ),
              },
            ]}
          >
            <Checkbox>
              I confirm this flow complies with Meta's WhatsApp Business Policy
              and contains no prohibited content
            </Checkbox>
          </Form.Item>

          <Space>
            <Button
              type="primary"
              ghost
              icon={<QuestionCircleOutlined />}
              onClick={showPreview}
            >
              Preview Flow
            </Button>

            <Button
              type="default"
              onClick={() => {
                const values = form.getFieldsValue(true);
              }}
            >
              Debug Values
            </Button>

            <Button
              type="primary"
              // loading={loading}
              icon={<CheckCircleOutlined />}
              htmlType="submit"
              disabled={validationErrors.length > 0}
            >
              Submit to Meta
            </Button>
          </Space>

          <Collapse ghost style={{ marginTop: 20 }}>
            <Panel header="Meta Validation Requirements" key="requirements">
              <ul>
                <li>
                  <strong>Flow Name:</strong> Max 100 chars, alphanumeric +
                  underscores/hyphens/spaces
                </li>
                <li>
                  <strong>Screens:</strong> Min 1, Max 20, unique IDs (max 50
                  chars)
                </li>
                <li>
                  <strong>Components:</strong> At least 1 per screen, max 3
                  buttons per screen
                </li>
                <li>
                  <strong>Options:</strong> Radio/Dropdown need min 2, Checkbox
                  needs min 1
                </li>
                <li>
                  <strong>Text Input:</strong> Max length between 1-120
                  characters
                </li>
                <li>
                  <strong>Navigation:</strong> No cyclic references allowed
                </li>
                <li>
                  <strong>Languages:</strong> Must use valid BCP-47 codes
                </li>
              </ul>
            </Panel>
          </Collapse>
        </Card>
      ),
    },
  ];

  const navigate = useNavigate();

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      setValidationErrors([]);

      const allFormValues = form.getFieldsValue(true);
      const finalValues = {
        flowName: values.flowName || allFormValues.flowName || flowName,
        description:
          values.description || allFormValues.description || description,
        entryPoint: values.entryPoint || allFormValues.entryPoint || entryPoint,
        flowType: values.flowType || allFormValues.flowType || flowType,
        languages:
          values.languages || allFormValues.languages || selectedLanguages,
        screens: values.screens || allFormValues.screens || screens,
        terms: values.terms || allFormValues.terms || acceptedTerms,
      };

      // Check if screens exist
      if (!finalValues.screens || finalValues.screens.length === 0) {
        message.error("Please add at least one screen to your flow");
        setValidationErrors(["Flow must have at least one screen"]);
        setLoading(false);
        return;
      }

      // Update state with final values
      setFlowName(finalValues.flowName);
      setDescription(finalValues.description || "");
      setEntryPoint(finalValues.entryPoint);
      setFlowType(finalValues.flowType);

      const newLanguages = finalValues.languages
        ? Array.isArray(finalValues.languages)
          ? finalValues.languages
          : [finalValues.languages]
        : ["en_US"];
      setSelectedLanguages(newLanguages);

      setScreens(finalValues.screens);
      setAcceptedTerms(finalValues.terms || false);

      // Run structural validation
      const structuralErrors = validateFlowStructure(finalValues);
      if (structuralErrors.length > 0) {
        setValidationErrors(structuralErrors);
        message.error("Please fix validation errors before submitting");
        setLoading(false);
        return;
      }

      // Convert your UI components to proper WhatsApp Flow JSON format
      const convertComponentToFlowFormat = (component) => {
        const baseComponent = {
          type: component.type,
          name: component.name,
          label: component.label || component.placeholder || component.name,
        };

        switch (component.type) {
          case "text_input":
            return {
              type: "TextInput",
              name: component.name,
              label: component.placeholder || "Enter text",
              "input-type": "text",
              required: component.required !== false,
              ...(component.max_length && {
                "max-length": parseInt(component.max_length),
              }),
            };

          case "email_input":
            return {
              type: "TextInput",
              name: component.name,
              label: component.placeholder || "Enter email",
              "input-type": "email",
              required: component.required !== false,
            };

          case "phone_input":
            return {
              type: "TextInput",
              name: component.name,
              label: component.placeholder || "Enter phone",
              "input-type": "phone",
              required: component.required !== false,
            };

          case "radio":
            return {
              type: "RadioButtonsGroup",
              name: component.name,
              label: component.label || "Select option",
              required: component.required !== false,
              "data-source": (component.options || []).map((opt) => ({
                id: opt.value || opt.id,
                title: opt.label || opt.value,
              })),
            };

          case "checkbox":
            return {
              type: "CheckboxGroup",
              name: component.name,
              label: component.label || "Select options",
              required: component.required !== false,
              "data-source": (component.options || []).map((opt) => ({
                id: opt.value || opt.id,
                title: opt.label || opt.value,
              })),
            };

          case "dropdown":
            return {
              type: "Dropdown",
              name: component.name,
              label: component.label || "Select option",
              required: component.required !== false,
              "data-source": (component.options || []).map((opt) => ({
                id: opt.value || opt.id,
                title: opt.label || opt.value,
              })),
            };

          case "date_picker":
            return {
              type: "DatePicker",
              name: component.name,
              label: component.label || "Select date",
              required: component.required !== false,
            };

          case "button":
            return {
              type: "Footer",
              label: component.label || "Continue",
              "on-click-action": {
                name: "complete",
                payload: {
                  action: component.action || "submit",
                },
              },
            };

          default:
            return baseComponent;
        }
      };

      // Build proper routing model - FIXED
      const buildRoutingModel = (screens) => {
        const routing = {};
        screens.forEach((screen, index) => {
          const screenId = screen.id.toUpperCase();
          if (index < screens.length - 1) {
            // Not the last screen - route to next screen
            const nextScreenId = screens[index + 1].id.toUpperCase();
            routing[screenId] = [nextScreenId];
          } else {
            // Last screen - empty array (terminal)
            routing[screenId] = [];
          }
        });
        return routing;
      };

      // Build the proper flow_json structure - UPDATED
      const flowJson = {
        version: "5.0",
        data_api_version: "3.0",
        routing_model: buildRoutingModel(finalValues.screens), // Fixed routing model
        screens: finalValues.screens.map((screen, index) => {
          const isLastScreen = index === finalValues.screens.length - 1;

          return {
            id: screen.id.toUpperCase(),
            title: screen.title,
            terminal: isLastScreen,
            success: isLastScreen, // Added success property for terminal screens
            data: {},
            layout: {
              type: "SingleColumnLayout",
              children: [
                // Use TextHeading instead of Text
                {
                  type: "TextHeading",
                  text: screen.title,
                },
                // Convert and add components
                ...(screen.components || [])
                  .map(convertComponentToFlowFormat)
                  .filter(Boolean),
              ],
            },
          };
        }),
      };

      // Create the flow data with correct structure - UPDATED
      const flowData = {
        name: finalValues.flowName,
        categories: ["OTHER"],
        endpoint_uri: "https://getyourticket.in/api/flow/flow-data",
        flow_json: JSON.stringify(flowJson),
      };

      console.log("Submitting flow data:", JSON.stringify(flowData, null, 2));

      const createRes = await apiClient.post("messaging/flows", flowData);

      console.log("Flow Created:", createRes.data);

      // Check for validation errors in successful response
      if (
        createRes.data?.validation_errors &&
        createRes.data.validation_errors.length > 0
      ) {
        console.error(
          "Flow validation errors:",
          createRes.data.validation_errors
        );
        const errorMessages = createRes.data.validation_errors.map(
          (err) => `${err.error_type}: ${err.message}`
        );
        setValidationErrors(errorMessages);
        message.error(
          "Flow created with validation errors. Please check the errors below."
        );
        return;
      }

      // If flow created successfully, publish it
      if (createRes.data?.id) {
        try {
          const publishRes = await apiClient.post(
            `messaging/flows/${createRes.data.id}/publish`
          );

          if (publishRes.status === 200) {
            console.log("Flow Published:", publishRes.data);
            message.success(
              "WhatsApp Flow created and published successfully!"
            );
            navigate(-1);
          } else {
            console.error("Failed to publish flow:", publishRes.data);
            message.warning(
              "Flow created but failed to publish. You can publish it manually from Facebook Business Manager."
            );
          }
        } catch (publishError) {
          console.error("Publishing error:", publishError);
          if (publishError.response?.data) {
            console.error("Publish API Error:", publishError.response.data);
            const errorMsg =
              publishError.response.data.error?.message ||
              "Unknown publish error";
            message.warning(
              `Flow created successfully but publishing failed: ${errorMsg}`
            );
          } else {
            message.warning(
              "Flow created successfully but publishing failed. You can publish it manually."
            );
          }
        }
      }
    } catch (error) {
      console.error("Flow submission error:", error);

      if (error.response?.data) {
        console.error("API Error Details:", error.response.data);

        // Handle validation errors from Meta API
        if (error.response.data.validation_errors) {
          const errorMessages = error.response.data.validation_errors.map(
            (err) =>
              `${err.error_type}: ${err.message} (Line ${err.line_start}-${err.line_end})`
          );
          setValidationErrors(errorMessages);
          message.error("Flow validation failed. Please fix the errors below.");
          return;
        }

        const errorMessage =
          error.response.data.error?.message || "Unknown API error";
        message.error(`Failed to create flow: ${errorMessage}`);

        // Handle specific validation errors from Meta
        if (error.response.data.error?.error_subcode) {
          setValidationErrors([
            `Meta API Error (${error.response.data.error.error_subcode}): ${errorMessage}`,
          ]);
        }
      } else {
        console.error("Error:", error.message);
        message.error(
          "Failed to submit WhatsApp Flow. Please check your connection and try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };
  // Preview modal content
  const renderPreviewModal = () => {
    // Use state variables instead of directly reading from the form
    return (
      <Modal
        title="Flow Preview"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={800}
      >
        <div style={{ background: "#f5f5f5", padding: 15, borderRadius: 8 }}>
          <Card title={flowName} style={{ marginBottom: 16 }}>
            <Space wrap>
              <Tag color="blue">
                {entryPoints.find((e) => e.value === entryPoint)?.label}
              </Tag>
              <Tag color="green">
                {flowTypes.find((f) => f.value === flowType)?.label}
              </Tag>
              {selectedLanguages.map((lang) => (
                <Tag key={lang} color="orange">
                  {languageOptions.find((l) => l.code === lang)?.name}
                </Tag>
              ))}
            </Space>

            <Divider />

            {description && <Text type="secondary">{description}</Text>}
          </Card>

          {screens.map((screen, index) => (
            <Card
              key={`screen-${screen.id}-${index}`}
              title={`Screen: ${screen.id}`}
              style={{ marginBottom: 16 }}
            >
              <Title level={5}>{screen.title}</Title>

              {screen.next_step_id && (
                <Text type="secondary">Next Step: {screen.next_step_id}</Text>
              )}

              <Divider />

              <div>
                <Text strong>Components:</Text>
                {(screen.components || []).map((component, compIndex) => (
                  <Card
                    key={compIndex}
                    type="inner"
                    title={
                      componentTypes.find((t) => t.value === component.type)
                        ?.label
                    }
                    style={{ marginTop: 8, marginBottom: 8 }}
                  >
                    <Text>
                      <strong>Name:</strong> {component.name}
                    </Text>
                    {component.placeholder && (
                      <>
                        <br />
                        <Text>
                          <strong>Placeholder:</strong> {component.placeholder}
                        </Text>
                      </>
                    )}
                    {component.label && (
                      <>
                        <br />
                        <Text>
                          <strong>Label:</strong> {component.label}
                        </Text>
                      </>
                    )}
                    {component.required !== undefined && (
                      <>
                        <br />
                        <Text>
                          <strong>Required:</strong>{" "}
                          {component.required ? "Yes" : "No"}
                        </Text>
                      </>
                    )}
                    {component.max_length && (
                      <>
                        <br />
                        <Text>
                          <strong>Max Length:</strong> {component.max_length}
                        </Text>
                      </>
                    )}
                    {component.action && (
                      <>
                        <br />
                        <Text>
                          <strong>Action:</strong>{" "}
                          {
                            buttonActions.find(
                              (a) => a.value === component.action
                            )?.label
                          }
                        </Text>
                      </>
                    )}

                    {component.options && component.options.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <Text strong>Options:</Text>
                        <div style={{ marginTop: 4 }}>
                          {component.options.map((option, optIndex) => (
                            <Tag
                              key={`${component.id}-option-${optIndex}`}
                              style={{ marginRight: 4, marginBottom: 4 }}
                            >
                              {option.label} ({option.value})
                            </Tag>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </Card>
          ))}

          {(!screens || screens.length === 0) && (
            <Alert
              message="No Screens"
              description="No screens have been added to this flow yet."
              type="warning"
              showIcon
            />
          )}
        </div>
      </Modal>
    );
  };
  return (
    <>
      <Row gutter={[16, 24]} justify="center">
        <Col xs={24} sm={24} md={20} lg={18} xl={16}>
          <Card>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <WhatsAppOutlined
                style={{ fontSize: 48, color: "#25D366", marginBottom: 16 }}
              />
              <Title level={2} style={{ margin: 0 }}>
                Create WhatsApp Flow
              </Title>
              <Paragraph>
                Design interactive experiences with full Meta validation
                compliance
              </Paragraph>
            </div>

            <Steps current={currentStep} style={{ marginBottom: 30 }}>
              {steps.map((item) => (
                <Step key={item.title} title={item.title} />
              ))}
            </Steps>

            <Form
              form={form}
              layout="vertical"
              initialValues={{
                flowName: flowName || "",
                description: description || "",
                entryPoint: entryPoint || "cta",
                flowType: flowType || "step_by_step",
                languages:
                  selectedLanguages.length > 0 ? selectedLanguages[0] : "en_US",
                screens: screens.length > 0 ? screens : [],
                terms: acceptedTerms || false,
              }}
              onValuesChange={updateFormState}
              onFinish={handleSubmit}
              requiredMark={true}
            >
              <div>{steps[currentStep].content}</div>

              <div
                style={{
                  marginTop: 24,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                {currentStep > 0 && (
                  <Button onClick={prevStep} icon={<ArrowLeftOutlined />}>
                    Previous
                  </Button>
                )}

                {currentStep < steps.length - 1 && (
                  <Button
                    type="primary"
                    onClick={nextStep}
                    style={{ marginLeft: "auto" }}
                  >
                    Next <ArrowRightOutlined />
                  </Button>
                )}
              </div>
            </Form>
          </Card>
        </Col>
      </Row>

      {renderPreviewModal()}
    </>
  );
};

export default NewFlow;
